// Google calendars in this browser: the accounts connected and their calendars (worker/gcal.ts status), signing in
// one more (a window that comes back to the board's own Google callback), and the events a Calendar board shows for
// the days around the one in view, read again when the board says a calendar changed.

import { create } from "zustand";
import type { GcalEvent, GcalStatus } from "../../shared/gcal";
import type { ID } from "../../shared/types";
import { api } from "../lib/api";

interface Span {
  board: ID;
  from: string;
  to: string;
}

interface GcalState {
  status: GcalStatus | null;
  /** what the last status read said went wrong */
  error: string | null;
  /** the events read for each board, for the span read, and what each calendar could not read */
  events: Record<ID, { span: Span; list: GcalEvent[]; problems: { cal: string; error: string }[]; at: number } | undefined>;
  /** a read under way, per board */
  reading: Record<ID, string | undefined>;
}

export const useGcal = create<GcalState>(() => ({ status: null, error: null, events: {}, reading: {} }));

export async function loadGcal(): Promise<GcalStatus> {
  try {
    const status = await api<GcalStatus>("/api/gcal/status");
    useGcal.setState({ status, error: null });
    return status;
  } catch (e) {
    useGcal.setState({ error: (e as Error).message });
    throw e;
  }
}

export async function refreshCalendars(account: string): Promise<GcalStatus> {
  const status = await api<GcalStatus>("/api/gcal/calendars", { body: { account } });
  useGcal.setState({ status });
  return status;
}

export async function removeAccount(account: string): Promise<GcalStatus> {
  const status = await api<GcalStatus>("/api/gcal/remove", { body: { account } });
  useGcal.setState({ status });
  rereadAll();
  return status;
}

/** A calendar taken off the board, put back. */
export async function restoreCalendar(account: string, cal: string): Promise<GcalStatus> {
  const status = await api<GcalStatus>("/api/gcal/restore", { body: { account, cal } });
  useGcal.setState({ status });
  rereadAll();
  return status;
}

/**
 * Connects a Google account, or the same one again to mend it: the window opens on the click, so the browser lets it,
 * then goes to Google; the board's callback page tells this tab when it is done, or the window closing does.
 */
export async function signInGcal(): Promise<GcalStatus> {
  const popup = window.open("about:blank", "cb-gcal-signin", "width=600,height=760");
  if (!popup) throw new Error("Allow a pop-up for Context Board, then try connecting Google Calendar again.");
  try {
    const before = useGcal.getState().status?.accounts.length ?? 0;
    const result = await api<{ authorizeUrl: string }>("/api/gcal/start", { body: {} });
    const origin = window.location.origin;
    return await new Promise<GcalStatus>((resolve, reject) => {
      let finished = false;
      // the callback page says when the sign-in failed: a Reconnect that failed leaves as many accounts as before
      let refused = false;
      const finish = async () => {
        if (finished) return;
        finished = true;
        clearInterval(poll);
        clearTimeout(deadline);
        window.removeEventListener("message", message);
        channel?.close();
        try {
          const status = await loadGcal();
          rereadAll();
          if (refused || status.accounts.length < before || !status.accounts.length) reject(new Error("Google sign-in did not finish. Try again, and tick both calendar permissions."));
          else resolve(status);
        } catch (e) {
          reject(e);
        }
      };
      const message = (e: MessageEvent) => {
        if (e.origin !== origin || e.source !== popup || e.data?.type !== "cb:mcp-auth" || e.data?.provider !== "google-calendar") return;
        refused = e.data.ok === false;
        void finish();
      };
      const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("cb-mcp-auth");
      if (channel)
        channel.onmessage = (e) => {
          if (e.data?.provider !== "google-calendar") return;
          refused = e.data.ok === false;
          void finish();
        };
      const poll = setInterval(() => {
        if (popup.closed) void finish();
      }, 700);
      const deadline = setTimeout(() => {
        popup.close();
        void finish();
      }, 15 * 60_000);
      window.addEventListener("message", message);
      popup.location.href = result.authorizeUrl;
    });
  } catch (e) {
    popup.close();
    throw e;
  }
}

/** Reads the Google events a board shows for a span of days, unless the same read is already there or under way. */
export async function readEvents(board: ID, from: string, to: string, force = false): Promise<void> {
  const s = useGcal.getState();
  const want = `${from}..${to}`;
  const have = s.events[board];
  if (!force && have && have.span.from === from && have.span.to === to) return;
  if (s.reading[board] === want && !force) return;
  useGcal.setState((st) => ({ reading: { ...st.reading, [board]: want } }));
  try {
    const r = await api<{ events: GcalEvent[]; problems: { cal: string; error: string }[] }>(`/api/gcal/events?board=${encodeURIComponent(board)}&from=${from}&to=${to}`);
    // a newer read asked for meanwhile wins
    if (useGcal.getState().reading[board] !== want) return;
    useGcal.setState((st) => ({ events: { ...st.events, [board]: { span: { board, from, to }, list: r.events, problems: r.problems, at: Date.now() } }, reading: { ...st.reading, [board]: undefined } }));
  } catch (e) {
    useGcal.setState((st) => ({ events: { ...st.events, [board]: { span: { board, from, to }, list: have?.list ?? [], problems: [{ cal: "*", error: (e as Error).message }], at: Date.now() } }, reading: { ...st.reading, [board]: undefined } }));
  }
}

/** Every board's Google events read again: a calendar changed, an account came or went. */
export function rereadAll() {
  const s = useGcal.getState();
  for (const [board, e] of Object.entries(s.events)) if (e) void readEvents(board, e.span.from, e.span.to, true);
}

// ---------------------------------------------------------------------------
// changing events in Google (worker/gcal.ts write)

export interface WriteEvent {
  title?: string;
  allDay?: boolean;
  start?: string;
  end?: string;
  tz?: string | null;
  location?: string | null;
  notes?: string | null;
  color?: string | null;
  repeat?: import("../../shared/repeat").RepeatRule | null;
  /** add a Google Meet call (step 5) */
  meet?: boolean;
  /** its guests by address, the whole list besides you */
  guests?: string[];
  /** the guests as the person saw them: when Google's differ, nothing is sent */
  guestsWere?: string[];
}
export interface WriteSpec {
  op: "create" | "update" | "delete" | "undelete" | "reply";
  /** Google emails the guests about it; left out, nobody */
  notify?: boolean;
  /** reply: your answer to an invitation */
  reply?: "accepted" | "tentative" | "declined";
  cal: string;
  id?: string;
  etag?: string;
  event?: WriteEvent;
  scope?: "one" | "following" | "all";
  series?: string;
  occ?: string;
  before?: { allDay: boolean; start: string; end: string; tz: string | null };
  pending?: string;
}
export interface WriteResult {
  ok: boolean;
  event?: GcalEvent;
  id?: string;
  pending?: string;
  clash?: { current: GcalEvent | null };
  gone?: boolean;
  /** who is asked changed in Google meanwhile: nothing was sent */
  guestsChanged?: boolean;
}

/** An event's guests and their answers, read from Google as its card opens, never kept; mine: you may change them. */
export interface Guests {
  guests: { email: string; name?: string; reply: import("../../shared/meet").Reply; organizer?: boolean; self?: boolean; optional?: boolean }[];
  mine: boolean;
}
export function readGuests(cal: string, id: string): Promise<Guests> {
  return api<Guests>(`/api/gcal/guests?cal=${encodeURIComponent(cal)}&id=${encodeURIComponent(id)}`);
}

/** One change to a Google event, sent to the board, which writes it to Google first and says what Google answered. */
export async function gcalWrite(spec: WriteSpec): Promise<WriteResult> {
  const r = await api<WriteResult>("/api/gcal/write", { body: spec });
  const edit = (keep: (x: GcalEvent) => boolean, add: GcalEvent[] = []) =>
    useGcal.setState((st) => ({ events: Object.fromEntries(Object.entries(st.events).map(([b, e]) => [b, e ? { ...e, list: [...e.list.filter(keep), ...add] } : e])) }));
  // what Google answered shows at once in the boards that read it (its newer version, after a clash); the rest
  // follows from the board's own word
  const ev = r.event ?? r.clash?.current;
  if (ev) edit((x) => !(x.cal === ev.cal && x.id === ev.id), [ev]);
  // a delete under way is hidden at once, as the board will hide it until it reaches Google
  if (spec.op === "delete" && r.ok) {
    const { cal, id, series, occ, scope = "one" } = spec;
    const of = (x: GcalEvent) => x.cal === cal && (x.id === id || (scope !== "one" && !!series && x.series === series && (scope === "all" || (!!occ && !!x.occ && x.occ >= occ))));
    edit((x) => !of(x));
  }
  return r;
}

/** Changes shown before Google answers (a drag let go), by the event's id on the board: they go when the answer comes. */
export const useGcalPending = create<{ shown: Record<string, Partial<WriteEvent> & { hidden?: boolean }> }>(() => ({ shown: {} }));
export function pendingShow(id: string, patch: (Partial<WriteEvent> & { hidden?: boolean }) | null) {
  useGcalPending.setState((s) => {
    const shown = { ...s.shown };
    if (patch) shown[id] = patch;
    else delete shown[id];
    return { shown };
  });
}

let soon: ReturnType<typeof setTimeout> | null = null;
/**
 * The board said calendars' events changed: every board's Google events are read again (a calendar newly shown has
 * none in the list yet), and the status too, a few changes at once read once.
 */
export function gcalChanged() {
  if (soon) clearTimeout(soon);
  soon = setTimeout(() => {
    soon = null;
    rereadAll();
    void loadGcal().catch(() => null);
  }, 250);
}
